import React, { useEffect, useMemo, useState } from "react";
import { api, fmtDate, fmtMoney, triggerFileDownload } from "@/lib/apiClient";
import { devLog } from "@/lib/devLog";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { FileZip, Download, CheckCircle } from "@phosphor-icons/react";

/**
 * BulkExportDialog — download every (or filtered / hand-picked) RA bill of a BOQ
 * rendered in the client's exact original Excel template, bundled as a ZIP.
 *
 * Modes:
 *   • "all"       – every non-cancelled RA bill
 *   • "submitted" – only status ∈ [approved, submitted, paid]
 *   • "pick"      – user checks specific bills
 */
export default function BulkExportDialog({ boqId, boqNo, onClose }) {
  const [ras, setRas] = useState([]);
  const [mode, setMode] = useState("all");
  const [checked, setChecked] = useState({});
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/ra-bills?boq_id=${boqId}`);
        setRas(data || []);
        // Default: pre-check everything so "pick" mode starts fully-selected
        const init = {};
        (data || []).forEach((r) => { init[r.id] = true; });
        setChecked(init);
      } catch (e) { devLog.warn(e); toast.error("Could not load RA bills"); }
      finally { setLoading(false); }
    })();
  }, [boqId]);  const eligibleCount = useMemo(() => {
    if (mode === "all") return ras.filter((r) => r.status !== "cancelled").length;
    if (mode === "submitted") return ras.filter((r) => ["approved", "submitted", "paid"].includes(r.status)).length;
    if (mode === "pick") return Object.values(checked).filter(Boolean).length;
    return 0;
  }, [mode, ras, checked]);

  const download = async () => {
    if (eligibleCount === 0) return toast.error("No RA bills match your selection");
    setBusy(true);
    try {
      let params = "";
      if (mode === "pick") {
        const ids = ras.filter((r) => checked[r.id]).map((r) => r.id).join(",");
        params = `ra_bill_ids=${encodeURIComponent(ids)}`;
      } else if (mode === "submitted") {
        params = "status_filter=approved_or_submitted";
      } else {
        params = "status_filter=all";
      }
      await triggerFileDownload(`/boqs/${boqId}/bulk-client-format-export?${params}`, `${boqNo || "BOQ"}-BulkClientFormat.zip`);
      toast.success(`Downloading ZIP with ${eligibleCount} RA bill(s)`);
      onClose?.();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  const toggle = (id) => setChecked((c) => ({ ...c, [id]: !c[id] }));
  const toggleAll = (v) => setChecked(Object.fromEntries(ras.map((r) => [r.id, v])));

  return (
    <Dialog open onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-2xl" data-testid="bulk-export-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display">
            <FileZip size={18} className="text-[#EA580C]" /> Bulk Client-Format Export
          </DialogTitle>
          <DialogDescription>
            Download every RA bill for <b>{boqNo}</b> rendered in the client's exact Excel template,
            bundled into a single ZIP with a manifest.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <RadioGroup value={mode} onValueChange={setMode} className="space-y-2">
            <div className="flex items-start gap-2 p-2.5 border border-slate-200 rounded-sm hover:border-[#EA580C]/40">
              <RadioGroupItem value="all" id="bulk-mode-all" data-testid="bulk-mode-all" className="mt-0.5" />
              <label htmlFor="bulk-mode-all" className="cursor-pointer flex-1">
                <div className="text-[12.5px] font-semibold text-slate-900">All RA bills</div>
                <div className="text-[11px] text-slate-500">Includes every non-cancelled bill · draft · verified · approved · submitted · paid</div>
              </label>
            </div>
            <div className="flex items-start gap-2 p-2.5 border border-slate-200 rounded-sm hover:border-[#EA580C]/40">
              <RadioGroupItem value="submitted" id="bulk-mode-submitted" data-testid="bulk-mode-submitted" className="mt-0.5" />
              <label htmlFor="bulk-mode-submitted" className="cursor-pointer flex-1">
                <div className="text-[12.5px] font-semibold text-slate-900">Approved & submitted only</div>
                <div className="text-[11px] text-slate-500">Skips drafts — only bills status ∈ [approved · submitted · paid]</div>
              </label>
            </div>
            <div className="flex items-start gap-2 p-2.5 border border-slate-200 rounded-sm hover:border-[#EA580C]/40">
              <RadioGroupItem value="pick" id="bulk-mode-pick" data-testid="bulk-mode-pick" className="mt-0.5" />
              <label htmlFor="bulk-mode-pick" className="cursor-pointer flex-1">
                <div className="text-[12.5px] font-semibold text-slate-900">Pick specific bills…</div>
                <div className="text-[11px] text-slate-500">Check the RA bills you want in the export</div>
              </label>
            </div>
          </RadioGroup>

          {mode === "pick" && (
            <div className="border border-slate-200 rounded-sm overflow-hidden" data-testid="bulk-picker">
              <div className="flex items-center justify-between px-2.5 py-1.5 bg-slate-50 border-b border-slate-200">
                <div className="text-[10.5px] font-semibold uppercase tracking-widest text-slate-600">
                  {eligibleCount} of {ras.length} selected
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" className="h-6 text-[10.5px]" onClick={() => toggleAll(true)}>Select all</Button>
                  <Button size="sm" variant="ghost" className="h-6 text-[10.5px]" onClick={() => toggleAll(false)}>Clear</Button>
                </div>
              </div>
              <div className="max-h-[220px] overflow-y-auto divide-y divide-slate-100">
                {loading ? (
                  <div className="text-[12px] text-slate-400 p-3 text-center">Loading…</div>
                ) : ras.length === 0 ? (
                  <div className="text-[12px] text-slate-400 p-3 text-center">No RA bills yet</div>
                ) : ras.map((r) => (
                  <label key={r.id} className="flex items-center gap-2.5 px-2.5 py-1.5 hover:bg-slate-50 cursor-pointer" data-testid={`bulk-pick-${r.id}`}>
                    <Checkbox checked={!!checked[r.id]} onCheckedChange={() => toggle(r.id)} />
                    <div className="flex-1 flex items-center gap-3 text-[11.5px]">
                      <span className="font-mono font-semibold w-16">{r.ra_bill_no}</span>
                      <span className="text-slate-500 w-24">{r.bill_date ? fmtDate(r.bill_date) : "—"}</span>
                      <span className="text-slate-800 flex-1 font-mono-num text-right">{fmtMoney(r.gross_value_this_bill)}</span>
                      <span className="text-[10px] uppercase tracking-widest text-slate-500 w-20">{r.status}</span>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className="text-[11px] text-slate-500 flex items-center gap-1.5" data-testid="bulk-count">
            <CheckCircle size={12} className="text-emerald-600" />
            {eligibleCount} RA bill(s) will be included in the ZIP.
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={onClose}>Cancel</Button>
          <Button
            onClick={download}
            disabled={busy || eligibleCount === 0}
            className="rounded-sm bg-[#EA580C] hover:bg-[#c2410c] text-white"
            data-testid="bulk-download-btn"
          >
            <Download size={13} className="mr-1" /> {busy ? "Preparing…" : `Download ZIP (${eligibleCount})`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
